import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Flex,
  List,
  Menu,
  Divider,
  message,
  Checkbox,
  Tooltip,
  Row,
  Col,
} from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import dayjs from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';

import {
  LeftArrowIcon,
  RightArrowIcon,
  UpArrowIcon,
  DownArrowIcon,
} from '@/assets/icons/icons';
import { searchFilter as SearchFilterIcon } from '@/assets/icons/icons';

import { useSpin } from '@/components/SpinContext';
import { useToken } from '@/common/helper';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { CustomPagination, CustomTitle, SearchInput } from '@/components';
import ShipmentListItem from '@/pages/HomeV2/partials/ChartPanel/ShipmentListItem';
import TeamCourierFilters from '@/pages/HomeV2/partials/ChartPanel/TeamCourierFilters';
import SelectAll from '@/assets/images/SelectAll.jsx';

import { MoreShipmentsCalendar } from '@/apis/shipment';

import type {
  CalendarMoreShipment,
  CalendarShipmentsRequestBody,
  CalendarViewBy,
} from '../../types';
import { getCalendarCustomerReference } from './calendarReferenceUtils';
import styles from './CalendarViewMore.module.css';
import { Shipment } from '@/pages/HomeV2/types';
import { ControlTowerView } from '../..';
type FilterValue = string | number | Record<string, unknown>;
type FilterArrayItem = {
  filterBy: string;
  filterValue: FilterValue | FilterValue[];
};
type SortOrderUi = 'asc' | 'desc';
type ApiSortOrder = 'ascending' | 'descending';
type ShipmentId = string;

type FiltersPayload = {
  teams?: (string | number)[];
  service_levels?: (string | number)[];
  [key: string]: unknown[] | undefined;
};
const maybeParseJson = (v: unknown) => {
  if (typeof v !== 'string') return v;

  const s = v.trim();
  const looksJson =
    (s.startsWith('{') && s.endsWith('}')) ||
    (s.startsWith('[') && s.endsWith(']'));

  if (!looksJson) return v;

  try {
    return JSON.parse(s);
  } catch {
    return v;
  }
};

const buildFiltersPayload = (
  data: FilterArrayItem[] | null | undefined
): FiltersPayload | null => {
  if (!data || data.length === 0) return null;

  const raw: Record<string, unknown[]> = {};
  const seen: Record<string, Set<string>> = {};

  const keyOf = (v: unknown) => {
    if (v === null) return 'null';
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') return `${t}:${v}`;
    try {
      return `obj:${JSON.stringify(v)}`;
    } catch {
      return `obj:${String(v)}`;
    }
  };

  data.forEach((item) => {
    const key = item.filterBy;
    if (!key) return;

    const values = Array.isArray(item.filterValue)
      ? item.filterValue
      : [item.filterValue];

    if (!raw[key]) raw[key] = [];
    if (!seen[key]) seen[key] = new Set<string>();

    values.forEach((val) => {
      const normalized = maybeParseJson(val);

      const k = keyOf(normalized);
      if (seen[key].has(k)) return;
      seen[key].add(k);

      raw[key].push(normalized);
    });
  });

  if (!Object.keys(raw).length) return null;

  const out: FiltersPayload = {};

  Object.entries(raw).forEach(([key, value]) => {
    if (key === 'team') out.teams = value as (string | number)[];
    else if (key === 'courier')
      out.service_levels = value as (string | number)[];
    else out[key] = value;
  });

  return out;
};
const baseSortOptions: Array<{ key: string; label: string }> = [
  { key: 'origin', label: 'Origin' },
  { key: 'destination', label: 'Destination' },
  { key: 'pickup_time', label: 'Pickup time' },
  { key: 'delivery_time', label: 'Delivery time' },
  { key: 'progress', label: 'Progress' },
  { key: 'timeliness', label: 'Timeliness' },
  { key: 'alert_status', label: 'Alert status' },
];

const toApiSortOrder = (ui: SortOrderUi): ApiSortOrder =>
  ui === 'asc' ? 'ascending' : 'descending';

const normalizeDateForApi = (value: string) => {
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : value;
};

const toId = (v: unknown): ShipmentId => String(v ?? '');

const getItemId = (item: CalendarMoreShipment): ShipmentId =>
  toId(item.id ?? item.shipment_id);

const getItemStatus = (
  item: CalendarMoreShipment
): Shipment['status'] | undefined =>
  // the API can send null here; callers already treat it like undefined
  item.status as Shipment['status'] | undefined;

const normalizeFilterArrayKey = (arr: FilterArrayItem[]) =>
  JSON.stringify(
    (arr ?? []).map((x) => ({
      filterBy: x.filterBy,
      filterValue: (Array.isArray(x.filterValue)
        ? x.filterValue
        : [x.filterValue]
      )
        .map(String)
        .sort(),
    }))
  );

const buildInitialTeamCourierFilterArray = (
  teams?: Array<string | number>,
  serviceLevels?: Array<string | number>
): FilterArrayItem[] => {
  const out: FilterArrayItem[] = [];

  if (Array.isArray(teams) && teams.length > 0) {
    out.push({ filterBy: 'team', filterValue: teams });
  }

  if (Array.isArray(serviceLevels) && serviceLevels.length > 0) {
    out.push({ filterBy: 'courier', filterValue: serviceLevels });
  }

  return out;
};

const mergeTeamCourierIntoFilters = (
  base: FilterArrayItem[],
  teamCourierOnly: FilterArrayItem[]
): FilterArrayItem[] => {
  const keep = (base ?? []).filter(
    (f) => f.filterBy !== 'team' && f.filterBy !== 'courier'
  );

  const tc = (teamCourierOnly ?? []).filter(
    (f) => f.filterBy === 'team' || f.filterBy === 'courier'
  );

  const map = new Map<string, FilterArrayItem>();
  [...keep, ...tc].forEach((f) => {
    if (!f?.filterBy) return;
    const vals = Array.isArray(f.filterValue) ? f.filterValue : [f.filterValue];
    map.set(f.filterBy, { filterBy: f.filterBy, filterValue: vals });
  });

  return Array.from(map.values());
};

interface CalendarViewMoreProps {
  initialSearch?: string;
  initialTeams?: Array<string | number>;
  initialServiceLevels?: Array<string | number>;
  initialPage?: number;
  onPageChange?: (p: number) => void;
  source?: 'header' | 'slot' | 'month';
  token: string;
  viewBy: CalendarViewBy;
  eventDatetime: string;
  onUnauthorized: (e: unknown) => void;
  tabValue: string;
  chartType?: string;
  onShipmentClick: (shipmentId: string) => void;
  slotStart?: string | null;
  slotEnd?: string | null;
  initialSortKey?: string;
  initialSortOrder?: SortOrderUi;
  onSortChange?: (sort: { key: string; order: SortOrderUi }) => void;
  legType?: 'CL' | 'EL';
  availableHourSlots?: string[];

  checked: boolean;

  selectedIds: ShipmentId[];
  setSelectedIds: React.Dispatch<React.SetStateAction<ShipmentId[]>>;
  /** When true, every row is selected except those in `excludedIds`. */
  isSelectAllMode?: boolean;
  excludedIds?: ShipmentId[];
  setExcludedIds?: React.Dispatch<React.SetStateAction<ShipmentId[]>>;
  setSelectedShipmentStatuses: React.Dispatch<
    React.SetStateAction<Record<ShipmentId, Shipment['status'] | undefined>>
  >;

  appliedFilterArray?: FilterArrayItem[];
  /** Same payload the main calendar uses (saved-set base + extras). */
  filtersPayload?: Record<string, unknown> | null;
  /** When `'active'`, drill-down sends `tab_id: 'active'` like the main calendar. */
  activeTabId?: string;
}

interface MoreShipmentsCalendarPayload {
  shipments?: CalendarMoreShipment[];
  total_pages?: number;
}

interface MoreShipmentsCalendarEnvelope {
  is_success: boolean;
  message?: string;
  data?: MoreShipmentsCalendarPayload;
}

interface MoreShipmentsCalendarResponse {
  data?: {
    data?: MoreShipmentsCalendarEnvelope;
  };
}

const CalendarViewMore: React.FC<CalendarViewMoreProps> = ({
  initialSearch,
  initialTeams,
  initialServiceLevels,
  initialPage,
  onPageChange,
  token,
  source,
  viewBy,
  eventDatetime,
  onUnauthorized,
  slotStart,
  slotEnd,
  tabValue,
  chartType,
  onShipmentClick,
  selectedIds,
  setSelectedIds,
  isSelectAllMode = false,
  excludedIds = [],
  setExcludedIds,
  setSelectedShipmentStatuses,
  checked,
  availableHourSlots = [],
  appliedFilterArray,
  filtersPayload: parentFiltersPayload,
  activeTabId,
  initialSortKey,
  initialSortOrder,
  onSortChange,
  legType,
}) => {
  const { decodedUserToken } = useToken();
  const enableCustomerLeg = Boolean(decodedUserToken()?.enablecustomerleg);
  const { formatDateTime } = useUserDateTimeFormat();
  const pendingFiltersRef = useRef<FilterArrayItem[] | null>(null);
  const flushTimerRef = useRef<number | null>(null);

  const isHourlyDrawer = source === 'slot' && !!slotStart && !!slotEnd;

  const defaultSort = useMemo((): { key: string; order: SortOrderUi } => {
    const usePickupDefault = source === 'header' || source === 'slot';
    return {
      key: usePickupDefault ? 'pickup_time' : 'delivery_time',
      order: usePickupDefault ? 'asc' : 'desc',
    };
  }, [source]);

  const [sortKey, setSortKey] = useState<string>(
    initialSortKey ?? defaultSort.key
  );
  const [sortOrder, setSortOrder] = useState<SortOrderUi>(
    initialSortOrder ?? defaultSort.order
  );

  const [page, setPage] = useState<number>(initialPage ?? 1);

  const [shipments, setShipments] = useState<CalendarMoreShipment[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [activeDate, setActiveDate] = useState<string>(() => {
    const seed = slotStart ? String(slotStart).slice(0, 10) : eventDatetime;
    return normalizeDateForApi(seed);
  });

  const [slotStartState, setSlotStartState] = useState<string | null>(
    slotStart ?? null
  );
  const [slotEndState, setSlotEndState] = useState<string | null>(
    slotEnd ?? null
  );

  const [activeEventDatetime, setActiveEventDatetime] = useState<string>(() => {
    const seed = slotStart ?? eventDatetime;
    return source === 'slot' && seed ? String(seed) : normalizeDateForApi(seed);
  });

  const initialTeamCourierFilters = useMemo(
    () =>
      buildInitialTeamCourierFilterArray(initialTeams, initialServiceLevels),
    [initialTeams, initialServiceLevels]
  );

  const seedAllFilters = useMemo<FilterArrayItem[]>(() => {
    const base = Array.isArray(appliedFilterArray) ? appliedFilterArray : [];
    return mergeTeamCourierIntoFilters(base, initialTeamCourierFilters);
  }, [appliedFilterArray, initialTeamCourierFilters]);

  const [filterArray, setFilterArray] = useState<FilterArrayItem[]>(
    () => seedAllFilters
  );

  const [hasTeamCourierOverride, setHasTeamCourierOverride] = useState(() => {
    return initialTeamCourierFilters.length > 0;
  });

  const [hasUserInteractedSearch, setHasUserInteractedSearch] =
    useState<boolean>(false);

  const initialSearchSafe = (initialSearch ?? '').trim();

  const [showSearch, setShowSearch] = useState<boolean>(
    initialSearchSafe.length > 0
  );
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchSafe);
  const [appliedSearch, setAppliedSearch] = useState<string>(initialSearchSafe);

  const [drawerMultiSelectEnabled, setDrawerMultiSelectEnabled] =
    useState(false);

  const showSelection = checked || drawerMultiSelectEnabled;

  const pageSize = 50;
  const { showSpin, hideSpin } = useSpin();

  const setPageAndNotify = useCallback(
    (p: number) => {
      setPage(p);
      onPageChange?.(p);
    },
    [onPageChange]
  );
  useEffect(() => {
    onSortChange?.({ key: sortKey, order: sortOrder });
  }, [sortKey, sortOrder, onSortChange]);
  const handleFiltersChange = useCallback(
    (next: FilterArrayItem[]) => {
      pendingFiltersRef.current = next;

      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }

      flushTimerRef.current = window.setTimeout(() => {
        const latestTeamCourier = pendingFiltersRef.current ?? [];
        pendingFiltersRef.current = null;

        setFilterArray((prev) => {
          const merged = mergeTeamCourierIntoFilters(
            prev ?? [],
            latestTeamCourier
          );
          const prevKey = normalizeFilterArrayKey(prev ?? []);
          const nextKey = normalizeFilterArrayKey(merged ?? []);
          if (prevKey === nextKey) return prev;
          return merged;
        });

        setHasTeamCourierOverride(true);
        setPageAndNotify(1);
      }, 0);
    },
    [setPageAndNotify]
  );

  useEffect(() => {
    if (hasUserInteractedSearch) return;

    const next = (initialSearch ?? '').trim();
    setSearchQuery(next);
    setAppliedSearch(next);
    setShowSearch(next.length > 0);
    // setPageAndNotify(1);
  }, [initialSearch, hasUserInteractedSearch]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (initialSortKey || initialSortOrder) return;

    setSortKey(defaultSort.key);
    setSortOrder(defaultSort.order);
  }, [defaultSort.key, defaultSort.order, initialSortKey, initialSortOrder]);
  useEffect(() => {
    setActiveDate(normalizeDateForApi(eventDatetime));
  }, [eventDatetime]);

  useEffect(() => {
    setSlotStartState(slotStart ?? null);
    setSlotEndState(slotEnd ?? null);

    if (source === 'slot' && slotStart) {
      setActiveEventDatetime(String(slotStart));
      setActiveDate(String(slotStart).slice(0, 10));
    } else {
      setActiveEventDatetime(normalizeDateForApi(eventDatetime));
      setActiveDate(normalizeDateForApi(eventDatetime));
    }
  }, [source, slotStart, slotEnd, eventDatetime]);

  useEffect(() => {
    const base = Array.isArray(appliedFilterArray) ? appliedFilterArray : [];
    const currentTeamCourier = (filterArray ?? []).filter(
      (f) => f.filterBy === 'team' || f.filterBy === 'courier'
    );
    const next = mergeTeamCourierIntoFilters(base, currentTeamCourier);

    setFilterArray((prev) => {
      const prevKey = normalizeFilterArrayKey(prev ?? []);
      const nextKey = normalizeFilterArrayKey(next ?? []);
      return prevKey === nextKey ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilterArray]);

  const headerText = useMemo(() => {
    const base = isHourlyDrawer && slotStartState ? slotStartState : activeDate;

    const datePart = dayjs(base).isValid()
      ? `${dayjs(base).format('ddd')}, ${formatDateTime(base, {
          includeTime: false,
          monthStyle: 'full',
        })}`
      : String(base ?? '');

    if (isHourlyDrawer && slotStartState && slotEndState) {
      const s = dayjs(slotStartState);
      const e = dayjs(slotEndState);
      if (s.isValid() && e.isValid()) {
        return `${datePart}, ${s.format('h A')} - ${e.format('h A')}`;
      }
    }

    return datePart;
  }, [
    activeDate,
    isHourlyDrawer,
    slotStartState,
    slotEndState,
    formatDateTime,
  ]);

  const activeSortLabel =
    baseSortOptions.find((o) => o.key === sortKey)?.label ?? 'Delivery time';

  const fetchMore = useCallback(
    async (
      p: number,
      sk: string,
      so: SortOrderUi,
      dateForApi: string,
      search: string,
      filters: FilterArrayItem[],
      useOverride: boolean
    ) => {
      if (!token || !viewBy || !dateForApi) return;

      const localFiltersPayload = buildFiltersPayload(filters);

      showSpin();
      try {
        const payload: CalendarShipmentsRequestBody = {
          view_by: viewBy,
          event_datetime: dateForApi,
          page: p,
          page_size: pageSize,
          sort_key: sk,
          sort_order: toApiSortOrder(so),
          search_query: (search ?? '').trim(),
          // Prefer the same filtersPayload the main calendar uses (saved-set
          // base + extras). Fall back to local filterArray only when absent
          // so additional-only filterArray cannot clobber saved-set bases.
          ...(parentFiltersPayload && typeof parentFiltersPayload === 'object'
            ? parentFiltersPayload
            : localFiltersPayload
              ? (localFiltersPayload as Record<string, unknown>)
              : {}),
          ...(activeTabId === 'active' ? { tab_id: 'active' } : {}),
          leg_type: legType,
        };

        if (useOverride) {
          payload.teams = localFiltersPayload?.teams ?? [];
          payload.service_levels = localFiltersPayload?.service_levels ?? [];
        }

        const res = (await MoreShipmentsCalendar(
          token,
          payload
        )) as MoreShipmentsCalendarResponse;

        const envelope = res?.data?.data;

        if (!envelope?.is_success) {
          message.error(envelope?.message || 'Error fetching shipments');
          setShipments([]);
          setTotalPages(1);
          return;
        }

        const data = envelope.data;
        const list = Array.isArray(data?.shipments) ? data!.shipments! : [];

        setShipments(list);
        setTotalPages(Number(data?.total_pages) || 1);
      } catch (e) {
        onUnauthorized(e);
      } finally {
        hideSpin();
      }
    },
    [
      token,
      viewBy,
      legType,
      showSpin,
      hideSpin,
      onUnauthorized,
      parentFiltersPayload,
      activeTabId,
    ]
  );

  useEffect(() => {
    // fetchMore handles its own errors; this just satisfies no-floating-promises
    fetchMore(
      page,
      sortKey,
      sortOrder,
      activeEventDatetime,
      appliedSearch,
      filterArray,
      hasTeamCourierOverride
    ).catch((): void => undefined);
  }, [
    page,
    sortKey,
    sortOrder,
    activeEventDatetime,
    appliedSearch,
    filterArray,
    hasTeamCourierOverride,
    fetchMore,
  ]);

  const availableSlots = useMemo(() => {
    const uniq = new Set((availableHourSlots ?? []).map(String));
    return Array.from(uniq).sort(
      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
    );
  }, [availableHourSlots]);

  const currentSlotIndex = useMemo(() => {
    if (!slotStartState) return -1;
    const cur = dayjs(slotStartState).valueOf();
    return availableSlots.findIndex((s) => dayjs(s).valueOf() === cur);
  }, [availableSlots, slotStartState]);

  const goPrevDate = () => {
    if (!isHourlyDrawer) {
      const d = dayjs(activeDate);
      const next = d.isValid()
        ? d.subtract(1, 'day').format('YYYY-MM-DD')
        : activeDate;

      setActiveDate(next);
      setActiveEventDatetime(next);
      setPageAndNotify(1);
      return;
    }

    if (currentSlotIndex <= 0) {
      message.error('No previous slot with shipments');
      return;
    }

    const start = availableSlots[currentSlotIndex - 1];
    const end = dayjs(start).add(1, 'hour').format('YYYY-MM-DDTHH:mm:ss');

    setSlotStartState(start);
    setSlotEndState(end);
    setActiveEventDatetime(start);
    setActiveDate(start.slice(0, 10));
    setPageAndNotify(1);
  };

  const goNextDate = () => {
    if (!isHourlyDrawer) {
      const d = dayjs(activeDate);
      const next = d.isValid()
        ? d.add(1, 'day').format('YYYY-MM-DD')
        : activeDate;

      setActiveDate(next);
      setActiveEventDatetime(next);
      setPageAndNotify(1);
      return;
    }

    if (
      currentSlotIndex === -1 ||
      currentSlotIndex >= availableSlots.length - 1
    ) {
      message.error('No next slot with shipments');
      return;
    }

    const start = availableSlots[currentSlotIndex + 1];
    const end = dayjs(start).add(1, 'hour').format('YYYY-MM-DDTHH:mm:ss');

    setSlotStartState(start);
    setSlotEndState(end);
    setActiveEventDatetime(start);
    setActiveDate(start.slice(0, 10));
    setPageAndNotify(1);
  };

  const selectedSet = useMemo(
    () => new Set((selectedIds ?? []).map(String)),
    [selectedIds]
  );
  const excludedSet = useMemo(
    () => new Set((excludedIds ?? []).map(String)),
    [excludedIds]
  );
  const isRowSelected = useCallback(
    (id: ShipmentId) =>
      isSelectAllMode
        ? !excludedSet.has(String(id))
        : selectedSet.has(String(id)),
    [isSelectAllMode, excludedSet, selectedSet]
  );

  const toggleOne = useCallback(
    (id: ShipmentId, isChecked: boolean, status?: Shipment['status']) => {
      const sid = String(id);

      if (isSelectAllMode) {
        setExcludedIds?.((prev) => {
          const next = new Set((prev ?? []).map(String));
          if (isChecked) next.delete(sid);
          else next.add(sid);
          return Array.from(next);
        });
        return;
      }

      setSelectedIds((prev) => {
        const next = new Set((prev ?? []).map(String));
        if (isChecked) next.add(sid);
        else next.delete(sid);
        return Array.from(next);
      });

      setSelectedShipmentStatuses((prev) => {
        const next: Record<ShipmentId, Shipment['status'] | undefined> = {
          ...(prev ?? {}),
        };
        if (isChecked) next[sid] = status;
        else delete next[sid];
        return next;
      });
    },
    [
      isSelectAllMode,
      setExcludedIds,
      setSelectedIds,
      setSelectedShipmentStatuses,
    ]
  );

  const handleSelectAll = useCallback(() => {
    const currentIds = (shipments ?? [])
      .map((it) => String(getItemId(it)))
      .filter(Boolean);

    if (currentIds.length === 0) {
      message.error('No shipments found on this page');
      return;
    }

    if (isSelectAllMode) {
      const allIncluded = currentIds.every((id) => !excludedSet.has(id));
      setExcludedIds?.((prev) => {
        const next = new Set((prev ?? []).map(String));
        currentIds.forEach((id) => {
          if (allIncluded) next.add(id);
          else next.delete(id);
        });
        return Array.from(next);
      });
      return;
    }

    const isAllSelected = currentIds.every((id) => selectedSet.has(id));

    if (isAllSelected) {
      const remove = new Set(currentIds);

      setSelectedIds((prev) =>
        (prev ?? []).map(String).filter((id) => !remove.has(id))
      );
      setSelectedShipmentStatuses((prev) => {
        const next = { ...(prev ?? {}) };
        currentIds.forEach((id) => delete next[id]);
        return next;
      });
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set((prev ?? []).map(String));
      currentIds.forEach((id) => next.add(id));
      return Array.from(next);
    });

    setSelectedShipmentStatuses((prev) => {
      const next = { ...(prev ?? {}) };
      (shipments ?? []).forEach((it) => {
        const id = String(getItemId(it));
        if (!id) return;
        if (!(id in next)) next[id] = getItemStatus(it);
      });
      return next;
    });
  }, [
    shipments,
    isSelectAllMode,
    excludedSet,
    setExcludedIds,
    selectedSet,
    setSelectedIds,
    setSelectedShipmentStatuses,
  ]);

  const sortMenu = (
    <Menu
      className='customSortMenu'
      selectedKeys={[sortKey]}
      onClick={(e) => {
        setSortKey(e.key);
        setPageAndNotify(1);
      }}
    >
      {baseSortOptions.map((opt) => (
        <Menu.Item key={opt.key}>{opt.label}</Menu.Item>
      ))}
    </Menu>
  );

  const getFormattedTime = (date?: string | null): string => {
    if (!date) return '---';
    return formatDateTime(date, { includeYear: false, includeTime: true });
  };

  const envView = process.env.REACT_APP_CONTROL_TOWER_VIEW as ControlTowerView;
  const isTableView = envView !== 'list';
  return (
    <>
      <div className={styles.dateNav}>
        <Flex gap={20} align='center'>
          <span
            className={`arrow-icon h-4 ${styles.dateNavArrow}`}
            onClick={goPrevDate}
          >
            <LeftArrowIcon />
          </span>
          <span
            className={`arrow-icon h-4 ${styles.dateNavArrow}`}
            onClick={goNextDate}
          >
            <RightArrowIcon />
          </span>
        </Flex>
      </div>

      <Flex vertical style={{ height: '100%', minHeight: 0 }}>
        <CustomTitle
          text={headerText}
          fontWeight='700'
          lineHeight='24px'
          className={
            isTableView ? 'calender-view-more-drawer-title' : 'font-18'
          }
        />

        <Divider
          style={{ margin: '10px 0', backgroundColor: 'var(--gray-300)' }}
        />

        <div className={`shipment-container ${styles.listContainer}`}>
          <div className='shipment-header-row'>
            <div
              className={`filters-search-wrapper ${isTableView ? 'filters-search-wrapper-responsive' : ''}`}
            >
              <Tooltip
                title={showSelection ? 'Select all' : 'Enable multi-select'}
                placement='top'
              >
                <div
                  onClick={() => {
                    if (!showSelection) {
                      setDrawerMultiSelectEnabled(true);
                      return;
                    }
                    handleSelectAll();
                  }}
                  className='select-all-responsive'
                >
                  <SelectAll />
                </div>
              </Tooltip>

              <TeamCourierFilters
                onFiltersChange={handleFiltersChange}
                initialTeams={initialTeams}
                initialServiceLevels={initialServiceLevels}
              />

              <Flex
                gap={4}
                className={`${isTableView && showSearch ? 'search-full-width' : 'flex-center y gap-2'}`}
              >
                {showSearch && (
                  <SearchInput
                    borderWidth='2px'
                    placeholder=''
                    defaultValue={searchQuery}
                    onChange={(val: string) => {
                      setHasUserInteractedSearch(true);
                      setSearchQuery(val);
                    }}
                    onPressEnter={(val) => {
                      if (showSearch) {
                        const next = val.trim();
                        setAppliedSearch(next);
                        setPageAndNotify(1);
                      } else {
                        setShowSearch(true);
                      }
                    }}
                    onClear={() => {
                      setHasUserInteractedSearch(true);
                      setSearchQuery('');
                      setAppliedSearch('');
                      setShowSearch(false);
                      setPageAndNotify(1);
                    }}
                    style={{ flex: 1, paddingRight: 10 }}
                    prefixIcon={<SearchFilterIcon width={12} height={12} />}
                  />
                )}

                <div
                  className={`search-toggle-btn ${showSearch ? 'open' : ''}`}
                  onClick={() => {
                    setHasUserInteractedSearch(true);
                    if (showSearch) {
                      const next = searchQuery.trim();
                      setAppliedSearch(next);
                      setPageAndNotify(1);
                    } else {
                      setShowSearch(true);
                    }
                  }}
                >
                  <SearchOutlined className='search-toggle-icon' />
                </div>
              </Flex>
            </div>

            <Dropdown overlay={sortMenu} trigger={['click']}>
              <Flex gap={6} align='center' className='cursor-pointer'>
                <CustomTitle
                  text='Sort by:'
                  fontWeight='400'
                  className={`${isTableView ? 'header-text-responsive' : 'font-12'}`}
                />
                <CustomTitle
                  marginTop={0}
                  text={activeSortLabel}
                  fontWeight='600'
                  className={`${isTableView ? 'header-text-responsive' : 'font-12'}`}
                />
                <div
                  className='sort-arrow-wrapper'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                    setPageAndNotify(1);
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <UpArrowIcon size={14} color='var(--text-primary)' />
                  ) : (
                    <DownArrowIcon size={14} color='var(--text-primary)' />
                  )}
                </div>
              </Flex>
            </Dropdown>
          </div>

          <Divider
            style={{ margin: '8px 0', backgroundColor: 'var(--gray-300)' }}
          />

          <div className={styles.listScroll}>
            <List
              dataSource={shipments}
              locale={{ emptyText: 'No shipments' }}
              renderItem={(item) => {
                const id = getItemId(item);
                const status = getItemStatus(item);
                const isItemChecked = isRowSelected(id);
                const itemWithCustomerReference = {
                  ...item,
                  reference_number: getCalendarCustomerReference(item),
                };

                return (
                  <List.Item style={{ padding: '12px 0' }}>
                    <Flex align='flex-start' gap={8} style={{ width: '100%' }}>
                      {showSelection && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={styles.checkboxCell}
                        >
                          <Checkbox
                            indeterminate={false}
                            checked={isItemChecked}
                            onChange={(e) =>
                              toggleOne(id, e.target.checked, status)
                            }
                          />
                        </div>
                      )}

                      <div className={styles.itemCell}>
                        <ShipmentListItem
                          isHome={false}
                          customerLegEnabled={enableCustomerLeg}
                          // calendar rows are a looser shape than the list's Shipment type
                          item={itemWithCustomerReference as Shipment}
                          tabValue={tabValue}
                          chartType={chartType}
                          getFormattedTime={getFormattedTime}
                          onClick={(sid) => onShipmentClick(String(sid))}
                        />
                      </div>
                    </Flex>
                  </List.Item>
                );
              }}
            />
          </div>

          <div className={styles.paginationFooter}>
            <Row style={{ width: '100%' }}>
              <Col flex='auto'>
                <Flex justify='flex-end'>
                  <CustomPagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={(newPage: number) =>
                      setPageAndNotify(newPage)
                    }
                  />
                </Flex>
              </Col>
            </Row>
          </div>
        </div>
      </Flex>
    </>
  );
};

export default CalendarViewMore;
